import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    untracked,
    ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLinkActive } from '@angular/router';
import type { Icon } from '@hyundai/assets';
import {
    AboutUsFeature,
    AnchorDirective,
    DealerFeature,
    LoginFeature,
    RouterLinkDirective,
    toLoaded,
} from '@hyundai/ng-common-lib';
import { EditableDirective } from '@hyundai/ng-edit-lib';
import {
    M000C010ImageComponent,
    M010C020IconLinkComponent,
    M030C040DefaultTeaserComponent,
    M030C050TeamAccordeonComponent,
    M040C020TeaserAllrounderComponent,
    M040C130ContactBannerComponent,
} from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { GlobalContactComponent } from '../../components/global-contact/global-contact.component';
import { ABOUT_US_TABS } from './about-us-infos';

@Component({
    selector: 'app-about-us-page',
    standalone: true,
    templateUrl: './about-us-page.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        EditableDirective,
        NgClass,
        RouterLinkDirective,
        M010C020IconLinkComponent,
        M030C040DefaultTeaserComponent,
        M030C050TeamAccordeonComponent,
        M040C020TeaserAllrounderComponent,
        M040C130ContactBannerComponent,
        M000C010ImageComponent,
        AnchorDirective,
        GlobalContactComponent,
        RouterLinkActive,
    ],
})
export class AboutUsPageComponent {
    ABOUT_US_TABS = ABOUT_US_TABS;
    store = inject(Store);
    route = inject(ActivatedRoute);

    dealer = this.store.selectSignal(DealerFeature.state.selectSubDealerOrDealer);
    mainDealer = this.store.selectSignal(DealerFeature.state.selectLoadedDealer);
    dealerLocationHeadline = this.store.selectSignal(DealerFeature.state.selectDealerLocationHeadline);
    dealerContactMailLink = this.store.selectSignal(DealerFeature.state.selectDealerContactFormUrl);
    aboutUsContent = this.store.selectSignal(AboutUsFeature.state.selectLoadedAboutUsContent);
    aboutUsTeaser = this.store.selectSignal(AboutUsFeature.state.selectTeasers);
    aboutUsEmployees = this.store.selectSignal(AboutUsFeature.state.selectLoadedAboutUsEmployees);
    jobOfferContent = toLoaded(this.store.selectSignal(AboutUsFeature.state.selectJobOfferContent));
    login = this.store.selectSignal(LoginFeature.state.selectIsLoggedIn);

    fragment = toSignal(this.route.fragment);

    locationLinks = computed(() => {
        const dealer = this.mainDealer();
        return [
            {
                name: dealer.city,
                link: dealer.pages.aboutUs,
            },
            ...dealer.subDealers.map((subDealer) => ({
                name: subDealer.city,
                link: subDealer.aboutUsUrl,
            })),
        ];
    });

    activeTab = computed(() => {
        return this.fragment() === ABOUT_US_TABS.JOBS ? ABOUT_US_TABS.JOBS : ABOUT_US_TABS.TEAM;
    });

    jobOfferBenefits = computed<Array<{ icon: Icon; text: string }>>(() => {
        const offer = this.jobOfferContent();
        if (!offer) return [];

        return offer.benefits.map((benefit) => ({
            icon: 'icon-check',
            text: benefit,
        }));
    });

    constructor() {
        effect(() => {
            this.dealer();
            untracked(() => {
                this.store.dispatch(AboutUsFeature.actions.loadAboutUsBelowTheFoldContent());
            });
        });
    }
}
